import {
    useState,
    useRef,
    useEffect,
    useCallback,
    useLayoutEffect,
    useContext,
    createContext,
    ReactNode,
    HTMLAttributes,
    MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'

// Lets DropdownItem close the menu after running its action, without relying on click bubbling
// (item handlers call e.stopPropagation() to protect the underlying row, which would otherwise
// suppress the menu's own close-on-click).
const DropdownCloseContext = createContext<() => void>(() => {})

// ==========================================
// Dropdown
// ==========================================

interface DropdownProps {
    trigger: ReactNode
    children: ReactNode
    align?: 'left' | 'right'
    className?: string
    onOpenChange?: (open: boolean) => void
}

interface MenuPosition {
    top: number
    left: number
    openAbove: boolean
}

export const Dropdown = ({ trigger, children, align = 'right', className = '', onOpenChange }: DropdownProps) => {
    const [open, setOpen] = useState(false)
    const triggerRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0, openAbove: false })

    const close = useCallback(() => setOpen(false), [])

    useEffect(() => {
        onOpenChange?.(open)
    }, [open, onOpenChange])

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return
        const rect = triggerRef.current.getBoundingClientRect()
        const menuHeight = menuRef.current?.offsetHeight ?? 200
        const menuWidth = menuRef.current?.offsetWidth ?? 180
        const spaceBelow = window.innerHeight - rect.bottom
        const openAbove = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8
        const top = openAbove ? rect.top - menuHeight - 4 : rect.bottom + 4
        const left = align === 'right' ? Math.max(8, rect.right - menuWidth) : rect.left
        setPosition({ top, left, openAbove })
    }, [align])

    useLayoutEffect(() => {
        if (open) updatePosition()
    }, [open, updatePosition])

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return
            close()
        }
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close()
        }
        const handleScroll = () => close()
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        window.addEventListener('scroll', handleScroll, true)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
            window.removeEventListener('scroll', handleScroll, true)
        }
    }, [open, close])

    return (
        <div ref={triggerRef} className={`relative inline-block ${className}`}>
            <div onClickCapture={() => setOpen((prev) => !prev)}>{trigger}</div>
            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        className="fixed z-[9999] min-w-[180px] rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:shadow-slate-900/50"
                        style={{ top: position.top, left: position.left }}
                        onClick={close}
                    >
                        <DropdownCloseContext.Provider value={close}>{children}</DropdownCloseContext.Provider>
                    </div>,
                    document.body
                )}
        </div>
    )
}

// ==========================================
// DropdownItem
// ==========================================

interface DropdownItemProps extends HTMLAttributes<HTMLButtonElement> {
    icon?: ReactNode
    variant?: 'default' | 'danger'
    disabled?: boolean
    children: ReactNode
}

export const DropdownItem = ({ icon, variant = 'default', disabled, children, className = '', onClick, ...props }: DropdownItemProps) => {
    const closeMenu = useContext(DropdownCloseContext)
    const variantClasses =
        variant === 'danger'
            ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950'
            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'

    const handleClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
        onClick?.(e)
        closeMenu()
    }

    return (
        <button
            type="button"
            disabled={disabled}
            className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors ${variantClasses} disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
            onClick={handleClick}
            {...props}
        >
            {icon && <span className="shrink-0 text-current">{icon}</span>}
            {children}
        </button>
    )
}

// ==========================================
// DropdownSeparator
// ==========================================

export const DropdownSeparator = () => <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
